// ============================================================
// dev-core-web/src/store/authStore.ts
// Version: 1.1.0
// Purpose:
//   - глобальное состояние аутентификации (Zustand)
//   - хранит actor, access token, scope, roles, clearance
//   - интегрируется с lib/api.ts через setTokenProvider
//   - интегрируется с lib/queryClient.ts через setUnauthorizedHandler
//   - НЕ хранит пароли, refresh tokens в localStorage
//   - access token — только в памяти (SECURITY.md §9.3, API.md §4.2)
//
// Ссылки:
//   - docs/API.md §4, §4.2, §4.4, §21.1
//   - docs/RBAC.md §4.1, §8.1
//   - docs/CLASSIFICATION.md §5.1
//   - docs/SECURITY.md §5, §9.3
//   - docs/ENTITIES.md §7.2
// ============================================================

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  api,
  setRefreshHandler,
  setTokenProvider,
  type ClassificationLabel,
} from '@/lib/api';
import { setUnauthorizedHandler } from '@/lib/queryClient';
import { clearClientStorage } from '@/lib/storage';
import type { ActorRef } from '@/lib/entitySchema';

// ------------------------------------------------------------
// Типы
// ------------------------------------------------------------

export interface AuthUser {
  id: string;                       // u_*
  email: string;
  displayName: string;
  status: 'active' | 'suspended' | 'invited' | 'deactivated';
  mfaEnabled: boolean;
  locale?: string;
  timezone?: string;
  roles: string[];
  teams: string[];
}

export interface ClearanceSummary {
  maxLevel: ClassificationLabel['level'];
  categories: string[];
  validUntil?: string;
  grantedBy: string;
  grantedAt: string;
}

export interface MfaChallenge {
  challengeId: string;
  methods: ('totp' | 'recovery' | 'webauthn' | 'enrol')[];
  enrolRequired: boolean;
}

export interface MfaEnrolOptions {
  otpauthUrl: string;
  secretBase32: string;
  pendingExpiresAt: string;
}

export interface AuthSnapshot {
  actor: ActorRef | null;
  user: AuthUser | null;
  accessToken: string | null;       // только в памяти
  expiresAt: number | null;         // ms since epoch
  clearance: ClearanceSummary | null;
  status:
    | 'idle'
    | 'loading'
    | 'mfa_required'
    | 'authenticated'
    | 'unauthenticated'
    | 'error';
  lastError?: string | undefined;
  /** Активный MFA-challenge входа (ADR-0008), пока не verified. */
  mfaChallenge: MfaChallenge | null;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginResult {
  user: AuthUser;
  accessToken: string;
  expiresAt: number;
  clearance: ClearanceSummary | null;
}

interface AuthState extends AuthSnapshot {
  // Actions
  login: (input: LoginInput) => Promise<AuthUser>;
  /** Второй фактор: totp/recovery по challenge → сессия. */
  verifyMfa: (input: {
    method: 'totp' | 'recovery';
    code: string;
  }) => Promise<AuthUser>;
  /** Bootstrap-энроллмент: options → QR (otpauth) → enrol → сессия. */
  enrolOptions: () => Promise<MfaEnrolOptions>;
  enrolMfa: (code: string) => Promise<{ user: AuthUser; recoveryCodes: string[] }>;
  /** WebAuthn-второй фактор: assertion → сессия (ADR-0008 Р6). */
  verifyWebauthn: (response: unknown) => Promise<AuthUser>;
  clearMfaChallenge: () => void;
  logout: (opts?: { silent?: boolean }) => Promise<void>;
  refresh: () => Promise<void>;
  loadMe: () => Promise<void>;
  applyUnauthenticated: () => void;
  hasRole: (role: string) => boolean;
  isAuthenticated: () => boolean;
}

// ------------------------------------------------------------
// Внутренние константы
// ------------------------------------------------------------

// Запас перед истечением access token — обновить заранее.
// Не универсальный TTL токена (это policy на сервере), а
// только клиентский skew для refresh.
const REFRESH_SKEW_MS = 30_000;

// Один in-flight refresh на весь store: refresh-cookie ротируется
// одноразово (API.md §21.1), параллельный вызов получил бы 401.
let refreshInFlight: Promise<void> | null = null;

// Поколение сессии: растёт при login/logout/разлогине. Ответ
// refresh, стартовавший до этих событий, не должен записать токен
// в уже другую (или несуществующую) сессию.
let authGeneration = 0;

// ------------------------------------------------------------
// MFA-хелперы (ADR-0008)
// ------------------------------------------------------------

type SetState = (partial: Partial<AuthState>) => void;

function applySession(set: SetState, response: LoginResult): AuthUser {
  const actor: ActorRef = { type: 'user', id: response.user.id };
  authGeneration += 1;
  set({
    actor,
    user: response.user,
    accessToken: response.accessToken,
    expiresAt: response.expiresAt,
    clearance: response.clearance,
    status: 'authenticated',
    lastError: undefined,
    mfaChallenge: null,
  });
  return response.user;
}

function extractMfaChallenge(err: unknown): MfaChallenge | null {
  if (!(err instanceof Error) || !('code' in err)) {
    return null;
  }
  const code = (err as { code?: unknown }).code;
  if (code !== 'MFA_REQUIRED' && code !== 'MFA_ENROLMENT_REQUIRED') {
    return null;
  }
  const details = (err as { details?: unknown }).details;
  if (typeof details !== 'object' || details === null) {
    return null;
  }
  const { challengeId, methods, enrolRequired } = details as {
    challengeId?: unknown;
    methods?: unknown;
    enrolRequired?: unknown;
  };
  if (typeof challengeId !== 'string') {
    return null;
  }
  const allowed = new Set(['totp', 'recovery', 'webauthn', 'enrol']);
  const fallback: MfaChallenge['methods'] = ['totp'];
  const parsedMethods: MfaChallenge['methods'] = Array.isArray(methods)
    ? methods.filter((m): m is MfaChallenge['methods'][number] =>
        typeof m === 'string' && allowed.has(m),
      )
    : fallback;
  return {
    challengeId,
    methods: parsedMethods.length > 0 ? parsedMethods : fallback,
    enrolRequired: enrolRequired === true,
  };
}

function isMfaError(err: unknown): boolean {
  if (!(err instanceof Error) || !('code' in err)) {
    return false;
  }
  const code = (err as { code?: unknown }).code;
  return (
    code === 'MFA_INVALID' ||
    code === 'MFA_CHALLENGE_EXPIRED' ||
    code === 'MFA_REQUIRED'
  );
}

// ------------------------------------------------------------
// Store
// ------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    actor: null,
    user: null,
    accessToken: null,
    expiresAt: null,
    clearance: null,
    status: 'idle',
    mfaChallenge: null,

    // ----------------------------------------------------------
    // login
    // ----------------------------------------------------------
    //
    // POST /api/v1/auth/login (API.md §21.1).
    // Возвращает access token + actor context.
    //
    // Refresh token (если используется cookie-flow) — сервер
    // устанавливает HttpOnly cookie сам; клиент его не видит
    // и не хранит (SECURITY.md §9.3, ADR-0002).

    login: async (input) => {
      set({ status: 'loading', lastError: undefined, mfaChallenge: null });

      try {
        const response = await api.post<LoginResult>('/auth/login', {
          email: input.email,
          password: input.password,
        });
        return applySession(set, response);
      } catch (err) {
        const mfa = extractMfaChallenge(err);
        if (mfa) {
          // Пароль верен — сервер требует второй фактор (ADR-0008 Р3).
          // Не ставим status:'error' — это отдельный шаг UI.
          set({ status: 'mfa_required', mfaChallenge: mfa, lastError: undefined });
          throw err;
        }
        set({
          status: 'error',
          lastError: err instanceof Error ? err.message : 'login failed',
        });
        throw err;
      }
    },

    // ----------------------------------------------------------
    // MFA verify / enrol (ADR-0008)
    // ----------------------------------------------------------

    verifyMfa: async ({ method, code }) => {
      const challenge = get().mfaChallenge;
      if (!challenge) {
        throw new Error('No MFA challenge');
      }
      set({ status: 'loading', lastError: undefined });
      try {
        const response = await api.post<LoginResult>('/auth/mfa/verify', {
          challengeId: challenge.challengeId,
          method,
          code,
        });
        return applySession(set, response);
      } catch (err) {
        const mfa = isMfaError(err);
        set({
          status: mfa ? 'mfa_required' : 'error',
          lastError:
            mfa || !(err instanceof Error)
              ? undefined
              : err.message,
        });
        throw err;
      }
    },

    enrolOptions: async () => {
      const challenge = get().mfaChallenge;
      if (!challenge) {
        throw new Error('No MFA challenge');
      }
      return api.post<MfaEnrolOptions>('/auth/mfa/enrol/options', {
        challengeId: challenge.challengeId,
      });
    },

    enrolMfa: async (code) => {
      const challenge = get().mfaChallenge;
      if (!challenge) {
        throw new Error('No MFA challenge');
      }
      set({ status: 'loading', lastError: undefined });
      try {
        const response = await api.post<
          LoginResult & { recoveryCodes: string[] }
        >('/auth/mfa/enrol', {
          challengeId: challenge.challengeId,
          code,
        });
        const user = applySession(set, response);
        return { user, recoveryCodes: response.recoveryCodes };
      } catch (err) {
        set({
          status: 'error',
          lastError: err instanceof Error ? err.message : 'enrolment failed',
        });
        throw err;
      }
    },

    verifyWebauthn: async (response) => {
      const challenge = get().mfaChallenge;
      if (!challenge) {
        throw new Error('No MFA challenge');
      }
      set({ status: 'loading', lastError: undefined });
      try {
        const res = await api.post<LoginResult>(
          '/auth/webauthn/login/verify',
          { challengeId: challenge.challengeId, response },
        );
        return applySession(set, res);
      } catch (err) {
        const mfa = isMfaError(err);
        set({
          status: mfa ? 'mfa_required' : 'error',
          lastError:
            mfa || !(err instanceof Error) ? undefined : err.message,
        });
        throw err;
      }
    },

    clearMfaChallenge: () => {
      set({
        mfaChallenge: null,
        status: get().accessToken ? 'authenticated' : 'unauthenticated',
        lastError: undefined,
      });
    },

    // ----------------------------------------------------------
    // logout
    // ----------------------------------------------------------
    //
    // POST /api/v1/auth/logout (API.md §21.1).
    // Даже если вызов не удался — клиентское состояние
    // сбрасывается (fail-safe: не оставляем stale token).

    logout: async (opts) => {
      try {
        if (!opts?.silent) {
          await api.post<void>('/auth/logout', undefined);
        }
      } catch {
        // Игнорируем сетевую ошибку — сессию всё равно
        // нужно очистить на клиенте.
      } finally {
        authGeneration += 1;
        set({
          actor: null,
          user: null,
          accessToken: null,
          expiresAt: null,
          clearance: null,
          status: 'unauthenticated',
          lastError: undefined,
        });
        clearClientStorage();
      }
    },

    // ----------------------------------------------------------
    // refresh
    // ----------------------------------------------------------
    //
    // POST /api/v1/auth/refresh (API.md §21.1).
    // Обновление access token. Refresh token — HttpOnly cookie,
    // клиент не видит (SECURITY.md §9.3).

    refresh: async () => {
      if (refreshInFlight) {
        await refreshInFlight;
        return;
      }

      refreshInFlight = (async () => {
        const gen = authGeneration;
        try {
          const response = await api.post<{
            accessToken: string;
            expiresAt: number;
            clearance?: ClearanceSummary;
          }>('/auth/refresh', undefined);

          // Logout/login успел произойти, пока ждали ответ:
          // не воскрешаем старую сессию и не затираем новую.
          if (gen !== authGeneration) return;

          set({
            accessToken: response.accessToken,
            expiresAt: response.expiresAt,
            clearance: response.clearance ?? get().clearance,
          });
        } catch (err) {
          if (gen !== authGeneration) throw err;

          const status =
            err instanceof Error && 'status' in err
              ? (err as { status?: number }).status
              : undefined;

          if (status === 401 || status === 403) {
            // Сессия истекла или отозвана — симметрично 401.
            get().applyUnauthenticated();
          } else if (status === undefined || status === 0 || status >= 500) {
            // Сетевой/серверный сбой — НЕ разлогиниваем:
            // пользователь просто не смог обновить токен.
            set({
              status: 'error',
              lastError: err instanceof Error ? err.message : 'refresh failed',
            });
          }
          throw err;
        }
      })();

      try {
        await refreshInFlight;
      } finally {
        refreshInFlight = null;
      }
    },

    // ----------------------------------------------------------
    // loadMe
    // ----------------------------------------------------------
    //
    // GET /api/v1/auth/me (API.md §21.1).
    // Используется при mount приложения, чтобы восстановить
    // actor context (если access token в памяти потерян,
    // например после hard refresh; тогда нужен отдельный
    // flow с refresh cookie).

    loadMe: async () => {
      set({ status: 'loading', lastError: undefined });

      // Cold reload: access token только в памяти, поэтому сначала
      // восстанавливаем его из refresh-cookie. Без этого GET /auth/me
      // ушёл бы без Bearer → 401 → разлогин на каждой перезагрузке.
      if (!get().accessToken) {
        try {
          await get().refresh();
        } catch {
          // refresh уже выставил статус (unauthenticated/error)
          return;
        }
      }

      try {
        const response = await api.get<{
          user: AuthUser;
          clearance: ClearanceSummary | null;
        }>('/auth/me');

        const actor: ActorRef = { type: 'user', id: response.user.id };

        set({
          actor,
          user: response.user,
          clearance: response.clearance,
          status: 'authenticated',
        });
      } catch (err) {
        if (err instanceof Error && 'status' in err) {
          const status = (err as { status?: number }).status;
          if (status === 401) {
            get().applyUnauthenticated();
            return;
          }
        }
        set({
          status: 'error',
          lastError: err instanceof Error ? err.message : 'loadMe failed',
        });
      }
    },

    // ----------------------------------------------------------
    // applyUnauthenticated
    // ----------------------------------------------------------
    //
    // Вызывается из queryClient.setUnauthorizedHandler при 401.
    // Не делает сетевых вызовов — уже неаутентифицированы.

    applyUnauthenticated: () => {
      authGeneration += 1;
      set({
        actor: null,
        user: null,
        accessToken: null,
        expiresAt: null,
        clearance: null,
        status: 'unauthenticated',
        lastError: undefined,
        mfaChallenge: null,
      });
      clearClientStorage();
    },

    // ----------------------------------------------------------
    // Helpers
    // ----------------------------------------------------------

    hasRole: (role) => {
      const user = get().user;
      if (!user) return false;
      return user.roles.includes(role);
    },

    isAuthenticated: () => {
      const { status, expiresAt } = get();
      if (status !== 'authenticated') return false;
      if (expiresAt === null) return false;
      return expiresAt - REFRESH_SKEW_MS > Date.now();
    },
  })),
);

// ------------------------------------------------------------
// Интеграция с lib/api.ts
// ------------------------------------------------------------
//
// Токен живёт в store. lib/api.ts не знает про Zustand —
// получает токен через провайдер. Это разрывает цикл
// authStore ↔ api.

setTokenProvider(() => useAuthStore.getState().accessToken);

// 401 из любого запроса → один refresh и повтор (см. lib/api.ts).
setRefreshHandler(() => useAuthStore.getState().refresh());

// ------------------------------------------------------------
// Интеграция с lib/queryClient.ts
// ------------------------------------------------------------
//
// 401 из любого query/mutation → сброс auth-состояния.
// setUnauthorizedHandler определён в lib/queryClient.ts.

setUnauthorizedHandler(() => {
  useAuthStore.getState().applyUnauthenticated();
});

// ------------------------------------------------------------
// Экспорт именованных селекторов
// ------------------------------------------------------------
//
// Компоненты используют селекторы, чтобы избежать лишних
// ре-рендеров при изменении несвязанных полей.

export const selectActor = (s: AuthState): ActorRef | null => s.actor;
export const selectUser = (s: AuthState): AuthUser | null => s.user;
export const selectClearance = (s: AuthState): ClearanceSummary | null => s.clearance;
export const selectAuthStatus = (s: AuthState): AuthState['status'] => s.status;